/**
 * AskHumanBox Component
 * Displays a prompt message and button to encourage users to ask their community
 */

import React from 'react';

import { useTranscriptContext } from '../../../../store/transcript/utils/useTranscriptContext';
import {
  AskHumanBoxContainer,
  AskHumanBoxContainerInFooter,
  AskHumanContainer,
  AskHumanContainerInFooter,
  AskHumanPrompt,
  AskHumanWrapper,
} from './AskHumanBox.styled';
import AskHumanButton from './AskHumanButton';
import { useOcAskHumanContext } from './OcAskHumanProvider';

export interface AskHumanBoxProps {
  /**
   * Text displayed in the prompt message
   * @default "Need opinions? AI can help you post."
   */
  promptText?: string;

  /**
   * Text displayed on the button
   * @default "Ask your fellow humans"
   */
  buttonText?: string;

  /**
   * Whether this box is displayed in the transcript footer (vs vertical sources panel)
   * Affects styling - removes top border radius and bottom margin
   * @default false
   */
  inTranscriptFooter?: boolean;

  /**
   * Optional className for styling
   */
  className?: string;
}

/**
 * AskHumanBox Component
 * Renders a call-to-action box with prompt text and a button to open the Ask Human modal
 */
const AskHumanBox: React.FC<AskHumanBoxProps> = ({
  promptText = 'Need opinions? AI can help you post.',
  buttonText = 'Ask your fellow humans',
  inTranscriptFooter = false,
  className = '',
}) => {
  const { toggleModal } = useOcAskHumanContext();
  const { state } = useTranscriptContext();

  // Disable button if there's no active session
  const hasSession = !!state.sessionId;

  // Use different styled components based on location
  const Container = inTranscriptFooter ? AskHumanContainerInFooter : AskHumanContainer;
  const BoxContainer = inTranscriptFooter ? AskHumanBoxContainerInFooter : AskHumanBoxContainer;

  return (
    <Container className={className}>
      <AskHumanWrapper>
        <BoxContainer>
          <AskHumanPrompt>{promptText}</AskHumanPrompt>
          <AskHumanButton 
            onClick={toggleModal} 
            text={buttonText} 
            disabled={!hasSession}
          />
        </BoxContainer>
      </AskHumanWrapper>
    </Container>
  );
};

export default AskHumanBox;

